import { useEffect, useRef } from 'react';
import { logSecurityEvent, getCachedUserId } from '@/utils/securityLogger';

const DEFAULT_THRESHOLD_MS = 10000;

/**
 * Detects "stuck" screens: a loading state (skeleton/spinner) that never
 * resolves, which the user experiences as a blank or frozen page. Emits a
 * Slow Load event so the admin dashboard can see it.
 */
export function useSlowLoadWatchdog(
  isLoading: boolean,
  label: string,
  thresholdMs: number = DEFAULT_THRESHOLD_MS
) {
  const reported = useRef(false);

  useEffect(() => {
    if (!isLoading) {
      reported.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (reported.current) return;
      reported.current = true;

      logSecurityEvent(
        typeof navigator !== 'undefined' && navigator.onLine === false
          ? 'client_network_timeout'
          : 'client_skeleton_timeout',
        getCachedUserId(),
        `Stuck loading: ${label} did not finish within ${Math.round(thresholdMs / 1000)}s`,
        {
          label,
          threshold_ms: thresholdMs,
          page_url: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
          online: typeof navigator !== 'undefined' ? navigator.onLine : true,
        }
      );
    }, thresholdMs);

    return () => clearTimeout(timer);
  }, [isLoading, label, thresholdMs]);
}
